import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Lock, Mail, ShieldCheck } from 'lucide-react';
import { Buton, Etiket, Girdi, toast } from '@emanetx/ui';
import { adminGirisIstegi, type AdminGirisIstegi } from '@emanetx/shared-types';
import { EmanetXHata } from '@emanetx/sdk';
import { useGirisYap, useOturumStore } from './oturum.js';

export function SayfaGiris() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const arama = useSearch({ strict: false }) as { 'suresi-doldu'?: string };
  const giris = useGirisYap();
  const mevcut = useOturumStore((s) => s.kullanici);

  useEffect(() => {
    if (mevcut) void navigate({ to: '/panel' });
  }, [mevcut, navigate]);

  const form = useForm<AdminGirisIstegi>({
    resolver: zodResolver(adminGirisIstegi),
    defaultValues: { eposta: '', parola: '' },
  });

  const golVer = form.handleSubmit(async (v) => {
    try {
      const k = await giris.mutateAsync(v);
      toast.success(`Hoş geldiniz, ${k.adSoyad}`);
      void navigate({ to: '/panel' });
    } catch (err) {
      const mesaj =
        err instanceof EmanetXHata && err.kod === 'gecersiz_kimlik'
          ? t('giris.hataGenel')
          : (err as Error).message || t('giris.hataGenel');
      form.setError('parola', { message: mesaj });
      toast.error(mesaj);
    }
  });

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      {/* Sol panel — koyu marka renkleriyle */}
      <div className="bg-fume-900 relative hidden overflow-hidden text-white lg:flex lg:flex-col lg:p-12 xl:p-16">
        {/* Pattern + glow */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" aria-hidden>
          <svg width="100%" height="100%">
            <defs>
              <pattern id="gp" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#gp)" />
          </svg>
        </div>
        <div
          className="bg-marka-500 pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full opacity-20 blur-3xl"
          aria-hidden
        />
        <div
          className="bg-marka-600 pointer-events-none absolute -bottom-40 -left-32 h-96 w-96 rounded-full opacity-15 blur-3xl"
          aria-hidden
        />

        <div className="relative flex h-full flex-col">
          <div className="flex items-center gap-3">
            <span className="bg-marka-500 text-fume-900 shadow-marka-500/30 inline-grid h-11 w-11 place-items-center rounded-xl font-black shadow-lg">
              E
            </span>
            <span className="text-2xl font-black tracking-tight">
              Emanet<span className="text-marka-400">X</span>
            </span>
          </div>

          <div className="mt-auto space-y-6">
            <h1 className="text-4xl font-bold leading-tight tracking-tight xl:text-5xl">
              {t('giris.heroBaslik')}
            </h1>
            <p className="text-fume-300 max-w-md text-lg">{t('giris.heroAlt')}</p>

            <ul className="grid gap-3 pt-4">
              {[
                'Tüm noktalarınız tek panelde',
                'Canlı dolap durumu + kiralama akışı',
                'Detaylı gelir + doluluk raporları',
              ].map((m) => (
                <li key={m} className="text-fume-200 flex items-center gap-3 text-sm">
                  <span className="bg-marka-500/15 text-marka-400 grid h-6 w-6 place-items-center rounded-full">
                    <ShieldCheck size={14} />
                  </span>
                  {m}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Sag panel — form */}
      <div className="dark:bg-fume-950 flex items-center justify-center bg-white p-6 sm:p-10">
        <div className="w-full max-w-sm space-y-7">
          {/* Mobil logo (sol panel gizliyken) */}
          <div className="flex items-center justify-center gap-2 lg:hidden">
            <span className="bg-marka-500 text-fume-900 inline-grid h-9 w-9 place-items-center rounded-lg font-black">
              E
            </span>
            <span className="text-fume-900 dark:text-fume-50 text-xl font-bold tracking-tight">
              Emanet<span className="text-marka-500">X</span>
            </span>
          </div>

          <header className="space-y-1.5">
            <h2 className="text-fume-900 dark:text-fume-50 text-2xl font-bold tracking-tight">
              {t('giris.baslik')}
            </h2>
            <p className="text-fume-500 dark:text-fume-400 text-sm">{t('giris.altBaslik')}</p>
          </header>

          {arama['suresi-doldu'] && (
            <div className="border-marka-200 bg-marka-50 text-marka-900 dark:border-marka-800 dark:bg-marka-900/30 dark:text-marka-200 rounded-lg border px-3.5 py-2.5 text-sm">
              {t('giris.suresiDoldu')}
            </div>
          )}

          <form onSubmit={golVer} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Etiket htmlFor="eposta">{t('giris.eposta')}</Etiket>
              <div className="relative">
                <Mail
                  size={16}
                  className="text-fume-400 pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2"
                />
                <Girdi
                  id="eposta"
                  type="email"
                  autoComplete="email"
                  placeholder="admin@emanetx.com"
                  className="pl-10"
                  {...form.register('eposta')}
                />
              </div>
              {form.formState.errors.eposta && (
                <p className="text-xs text-red-600">{form.formState.errors.eposta.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Etiket htmlFor="parola">{t('giris.parola')}</Etiket>
                <button
                  type="button"
                  className="text-marka-700 hover:text-marka-800 dark:text-marka-400 text-xs font-medium hover:underline"
                  onClick={() =>
                    toast.info(
                      'Şifre sıfırlama akışı sonraki adımda eklenecek. Şu an admin parolasını rotate etmek için sistem yöneticisi ile görüşün.',
                    )
                  }
                >
                  {t('giris.parolaUnut')}
                </button>
              </div>
              <div className="relative">
                <Lock
                  size={16}
                  className="text-fume-400 pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2"
                />
                <Girdi
                  id="parola"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="pl-10"
                  {...form.register('parola')}
                />
              </div>
              {form.formState.errors.parola && (
                <p className="text-xs text-red-600">{form.formState.errors.parola.message}</p>
              )}
            </div>

            <Buton type="submit" boyut="buyuk" className="w-full" disabled={giris.isPending}>
              {giris.isPending ? `${t('giris.giris')}...` : t('giris.giris')}
            </Buton>
          </form>

          <p className="text-fume-500 dark:text-fume-400 text-center text-xs">
            {t('giris.yetkili')}{' '}
            <a
              href="mailto:info@emanetx.com"
              className="text-marka-700 dark:text-marka-400 font-medium hover:underline"
            >
              {t('giris.iletisim')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
